import { useTranslation } from 'react-i18next';
import React, { useRef, useState } from 'react';
import _ from 'lodash';
import { isValidPhoneNumber } from 'react-phone-number-input';
import pt from 'react-phone-number-input/locale/pt';
import en from 'react-phone-number-input/locale/en';
import es from 'react-phone-number-input/locale/es';
import fr from 'react-phone-number-input/locale/fr';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { MobileModalBody } from '../../components/profile/mobile-modal-body';
import { MobileModalTitle } from '../../components/profile/mobile-modal-title';
import { layoutDefaults } from '../../utils/layout-defaults';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { usePublicAPI } from '../../apis/public-api';
import { useAccountMobileAPI } from '../../apis/account-mobile-api';
import { useErrorHandler } from '../../hooks/use-error-handler';

function useMobileModal() {
  const storage = useStorage();
  const { menuInfo, language } = storage;
  const analytics = useAnalytics();
  const { getPhoneNumberPrivacyPolicyFromRemote } = usePublicAPI();
  const { loadAccountMobileFromRemote, updateAccountMobileFromRemote } =
    useAccountMobileAPI();
  const { handleErrorOnModal } = useErrorHandler();
  const { swal } = useSwal({ storage });
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const defaultCountryRef = useRef();
  const defaultPhoneNumberRef = useRef();
  const defaultLabelsRef = useRef();

  function loadLabels() {
    const code =
      language?.code?.language ??
      menuInfo?.i18n?.default_language?.code?.default_language;

    let module;

    switch (code) {
      case 'pt':
        module = pt;
        break;
      case 'en':
        module = en;
        break;
      case 'es':
        module = es;
        break;
      case 'fr':
        module = fr;
        break;
      default:
        return en;
    }

    return module;
  }

  async function prepare() {
    defaultCountryRef.current = null;
    defaultPhoneNumberRef.current = null;
    defaultLabelsRef.current = loadLabels();

    const response = await loadAccountMobileFromRemote();
    const mobile = response.data;

    if (_.isEmpty(mobile)) {
      const countryCode =
        menuInfo.address?.country?.code ??
        menuInfo?.i18n?.default_language?.code?.region;

      defaultCountryRef.current = countryCode;
    }

    defaultPhoneNumberRef.current = mobile;

    console.log('response', response.data);
  }

  async function show({
    showBackButton,
    prepareOnPreConfirm,
    prepareOnPreDeny,
  }) {
    let phone = null;

    const result = await swal.fire({
      title: (
        <MobileModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          title={t('mobile-modal.title')}
          analytics={analytics}
          getPrivacyPolicyFromRemote={getPhoneNumberPrivacyPolicyFromRemote}
        />
      ),
      html: (
        <MobileModalBody
          defaultCountry={defaultCountryRef.current}
          defaultPhoneNumber={defaultPhoneNumberRef.current}
          onPhoneChange={(pPhone) => {
            phone = pPhone;
            swal.resetValidationMessage();
          }}
          disabled={loading}
          labels={defaultLabelsRef.current}
          // labels={pt}
        />
      ),
      showConfirmButton: true,
      showDenyButton: true,
      showCancelButton: false,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      preConfirm: () => preConfirm({ prepareOnPreConfirm, phone }),
      preDeny: () => preDeny({ prepareOnPreDeny }),
      confirmButtonText: showBackButton ? t('continue') : t('save'),
      denyButtonText: showBackButton ? t('back') : t('cancel'),
    });

    return result;
  }

  async function preConfirm({ prepareOnPreConfirm, phone }) {
    setLoading(true);

    if (_.isEmpty(phone)) {
      swal.showValidationMessage(t('mobile-modal.empty'));
      return false;
    }

    if (!isValidPhoneNumber(phone)) {
      swal.showValidationMessage(t('mobile-modal.invalid'));
      return false;
    }

    try {
      await updateAccountMobileFromRemote(phone);
      await prepareOnPreConfirm?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }

  async function preDeny({ prepareOnPreDeny }) {
    setLoading(true);

    try {
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }

  return {
    prepareMobileModal: prepare,
    showMobileModal: show,
  };
}

export { useMobileModal };
